import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import React, { useRef, useState } from "react";
import { MdDelete } from "react-icons/md";
/*
  The inner uploader, NOT ATMFilePickerWrapper. The wrapper renders Formik's
  <ErrorMessage>, which reads the Formik context and throws when there is no
  <Formik> above it — this dialog holds its own state, so mounting the wrapper
  here crashed the dialog before it ever painted. Row errors are rendered below
  by hand instead.
*/
import ATMFileUploader from "src/components/UI/atoms/formFields/ATMFileUploader/ATMFileUploader";
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";
// The same crop / rotate / tone tool the PAN form uses for the photograph, the
// signature and every attached document — so an operator who has cropped a scan
// there has already learnt this one.
import ImageCropInput from "../PanFormDialogBox/ImageCropInput";
import { showToast } from "src/utils/toaster/showToast";
import { STAFF_FILE_REMARK_MAX_LENGTH } from "./staffFileRules";

export type StaffFileRow = {
  /** Local key — the rows are reorderable by deletion, so the index is not one. */
  rowId: string;
  title: string;
  remark: string;
  file: File | null;
  /**
   * The picked image as a data-URL, and the crop tool's working copy.
   *
   * Only ever set for an image; a PDF row leaves it null, which is what hides
   * Crop / Adjust for it. A data-URL rather than `URL.createObjectURL` on
   * purpose: the crop tool reads the image back off a canvas, and a data-URL
   * can never taint one.
   */
  imageSrc: string | null;
};

/**
 * Longest side of a cropped image, in px.
 *
 * These are documents that may be appended to the PAN form as a full A4 page,
 * and 200 DPI across 210 mm needs 1654 px — the same reasoning, and the same
 * number, as the PAN form's own attachment crop panel.
 */
const CROPPED_MAX_SIZE = 2000;

/**
 * Re-encode the crop tool's output as JPEG before uploading it.
 *
 * `ImageCropInput` emits PNG, which is right for something being pasted into
 * the posted HTML but wrong for an upload: a 2000px PNG of a photographed
 * document runs to several MB where the JPEG is a few hundred KB. The API
 * compresses images with sharp anyway, so this only saves the operator the
 * upload — it costs nothing at the far end.
 */
const dataUrlToJpegFile = (
  dataUrl: string,
  baseName: string
): Promise<File | null> =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(null);
        return;
      }
      // Paint white first: JPEG has no alpha, and a transparent pixel handed
      // straight to it comes out black.
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      canvas.toBlob(
        (blob) =>
          resolve(
            blob
              ? new File([blob], `${baseName}.jpg`, { type: "image/jpeg" })
              : null
          ),
        "image/jpeg",
        0.9
      );
    };
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });

/**
 * The crop workspace for one row.
 *
 * A component rather than a few props inline for the reason the PAN form's
 * attachment panel is one: `autoEdit` opens the workspace from an effect, so
 * the first `onEditingChange` of the mount is the widget reporting "not open
 * yet", and closing on that would shut the panel on the frame it appeared.
 * `opened` guards that; it is cleared as the panel reports an ending so a
 * re-render on the way out cannot report the same ending twice.
 */
const RowCropPanel = ({
  src,
  onCropped,
  onClose,
}: {
  src: string;
  onCropped: (dataUrl: string) => void;
  onClose: () => void;
}) => {
  const opened = useRef(false);
  const onEditingChange = React.useCallback(
    (editing: boolean) => {
      if (editing) {
        opened.current = true;
        return;
      }
      if (!opened.current) return;
      opened.current = false;
      // Both endings close the panel — Crop & Use has already handed the new
      // image back through onCropped, and Cancel changed nothing.
      onClose();
    },
    [onClose]
  );

  return (
    <div className="border border-slate-300 rounded p-2 bg-slate-50">
      <ImageCropInput
        label=""
        itemName="file"
        maxSize={CROPPED_MAX_SIZE}
        // The panel only exists because the operator clicked Crop / Adjust, so
        // it opens straight into the workspace rather than on another preview.
        autoEdit
        grayscaleByDefault
        value={src}
        onChange={onCropped}
        onEditingChange={onEditingChange}
      />
    </div>
  );
};

type Props = {
  onClose: () => void;
  /**
   * Uploads ONE row. The dialog walks the rows itself rather than posting them
   * together: one rejected file (wrong type, too large, status moved on) must
   * not take the other files down with it.
   *
   * Resolves to an error message, or null when the row was accepted.
   */
  onUploadRow: (row: StaffFileRow) => Promise<string | null>;
  isUploading: boolean;
};

const newRow = (): StaffFileRow => ({
  rowId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  title: "",
  remark: "",
  file: null,
  imageSrc: null,
});

const AddMoreFileDialog = ({ onClose, onUploadRow, isUploading }: Props) => {
  const [rows, setRows] = useState<StaffFileRow[]>([newRow()]);
  // Per-row outcome after a submit, so a partial failure says WHICH row failed
  // rather than collapsing to one toast.
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  /*
    Which row has its crop workspace open, or null. One at a time: the
    workspace is tall, and two of them open in a dialog puts the Upload button
    somewhere nobody can find it.
  */
  const [cropRowId, setCropRowId] = useState<string | null>(null);
  /*
    The file each row is currently reading, so a read that finishes after the
    operator has picked something else can tell that it is stale. A ref, not
    state: nothing renders from it, and it has to be readable from inside the
    FileReader callback without re-subscribing.
  */
  const pendingPick = useRef<Record<string, File | null>>({});

  const updateRow = (rowId: string, patch: Partial<StaffFileRow>) => {
    setRows((current) =>
      current.map((row) => (row.rowId === rowId ? { ...row, ...patch } : row))
    );
  };

  /**
   * A file was picked (or cleared) on a row.
   *
   * An image is read into `imageSrc` and the crop workspace opens on it
   * STRAIGHT AWAY — the same thing the applicant-facing uploader does, and the
   * thing the operator wants nine times out of ten, since these files are
   * phone photos of a document that need straightening before they are any use.
   * Leaving the crop behind a second button meant most files went up untouched.
   * Cancel in the workspace keeps the file exactly as it was picked.
   *
   * The read is async, so the row is re-checked when it lands: the operator can
   * pick a second file while the first is still being read, and the stale
   * result must not overwrite it — nor open a workspace on the wrong image.
   */
  const handlePickFile = (rowId: string, picked: File | null) => {
    updateRow(rowId, { file: picked, imageSrc: null });
    if (cropRowId === rowId) setCropRowId(null);
    if (!picked || !picked.type?.startsWith("image/")) return;

    pendingPick.current[rowId] = picked;
    const reader = new FileReader();
    reader.onload = () => {
      // Superseded while it was being read — a third pick has already claimed
      // the row, and opening a workspace on this image would be the wrong one.
      if (pendingPick.current[rowId] !== picked) return;
      const src = reader.result as string;
      setRows((current) =>
        current.map((row) =>
          row.rowId === rowId ? { ...row, imageSrc: src } : row
        )
      );
      setCropRowId(rowId);
    };
    reader.readAsDataURL(picked);
  };

  /**
   * Crop & Use came back. The cropped image REPLACES the picked file — it is
   * what gets uploaded — while the original stays on disk untouched, exactly
   * as the PAN form's crop tool leaves the applicant's own files alone.
   */
  const handleCropped = async (row: StaffFileRow, dataUrl: string) => {
    const base =
      (row.file?.name || "").replace(/\.[^.]+$/, "").trim() || "document";
    const cropped = await dataUrlToJpegFile(dataUrl, `${base}-cropped`);
    if (!cropped) {
      showToast(
        "error",
        "Could not save the cropped image. The file you picked is still selected."
      );
      return;
    }
    // `imageSrc` keeps the crop tool's own output, so re-opening Crop / Adjust
    // continues from what is on screen rather than from the original file.
    updateRow(row.rowId, { file: cropped, imageSrc: dataUrl });
  };

  const removeRow = (rowId: string) => {
    setRows((current) =>
      current.length === 1 ? current : current.filter((row) => row.rowId !== rowId)
    );
    if (cropRowId === rowId) setCropRowId(null);
  };

  const handleSubmit = async () => {
    const filledRows = rows.filter((row) => row.file);
    if (!filledRows.length) {
      showToast("error", "Select at least one file to upload.");
      return;
    }
    const untitled = filledRows.find((row) => !row.title.trim());
    if (untitled) {
      showToast("error", "Every file needs a title.");
      return;
    }

    const errors: Record<string, string> = {};
    const uploaded: string[] = [];

    // Sequential, not Promise.all: these all write to the same document, and a
    // burst of concurrent $push calls is the one thing worth not doing here.
    for (const row of filledRows) {
      // eslint-disable-next-line no-await-in-loop
      const error = await onUploadRow(row);
      if (error) {
        errors[row.rowId] = error;
      } else {
        uploaded.push(row.rowId);
      }
    }

    setRowErrors(errors);
    // Drop what landed; keep what failed so it can be retried in place.
    setRows((current) => {
      const remaining = current.filter((row) => !uploaded.includes(row.rowId));
      return remaining.length ? remaining : [newRow()];
    });

    if (uploaded.length) {
      showToast(
        "success",
        `${uploaded.length} file${uploaded.length > 1 ? "s" : ""} attached.`
      );
    }
    if (!Object.keys(errors).length) {
      onClose();
    }
  };

  return (
    /*
      Wider while cropping. The dialog is `sm` (600px) for a list of title /
      remark / file rows, which is the right size for them — but a document
      scan placed inside 600px is too small to drag an accurate crop box on.
    */
    <Dialog open={true} maxWidth={cropRowId ? "md" : "sm"} fullWidth>
      <DialogTitle>
        <div className="flex justify-start text-primary-main font-bold">
          Add More File
        </div>
      </DialogTitle>
      <DialogContent>
        <div className="flex flex-col gap-4 pt-1">
          <div className="text-[12px] text-slate-500">
            Images and PDF files only. The remark is shown with the file on this
            screen and is not used anywhere else.
          </div>

          {rows.map((row, rowIndex) => (
            <div
              key={row.rowId}
              className="border border-slate-300 rounded p-3 flex flex-col gap-3"
            >
              <div className="flex items-center justify-between">
                <div className="text-[13px] font-medium text-slate-700">
                  File {rowIndex + 1}
                </div>
                {rows.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeRow(row.rowId)}
                    disabled={isUploading}
                    className="text-red-500 disabled:opacity-40"
                    title="Remove this row"
                  >
                    <MdDelete size={18} />
                  </button>
                )}
              </div>

              <div>
                <label className="text-[12px] font-medium text-slate-600">
                  Title *
                </label>
                <input
                  type="text"
                  value={row.title}
                  disabled={isUploading}
                  onChange={(e) => updateRow(row.rowId, { title: e.target.value })}
                  placeholder="e.g. Address proof"
                  className="w-full border border-slate-300 rounded px-3 py-2 text-sm mt-1"
                />
              </div>

              <div>
                <label className="text-[12px] font-medium text-slate-600">
                  Remark
                </label>
                <textarea
                  value={row.remark}
                  disabled={isUploading}
                  maxLength={STAFF_FILE_REMARK_MAX_LENGTH}
                  onChange={(e) =>
                    updateRow(row.rowId, { remark: e.target.value })
                  }
                  placeholder="Optional note about this file"
                  rows={2}
                  className="w-full border border-slate-300 rounded px-3 py-2 text-sm mt-1"
                />
                {/* The textarea caps typing silently, so the count has to say so. */}
                <div className="text-right text-[11px] text-slate-500">
                  {row.remark.length}/{STAFF_FILE_REMARK_MAX_LENGTH}
                </div>
              </div>

              <ATMFileUploader
                label="File"
                required
                placeholder="Select image or PDF"
                selectedFile={row.file}
                disabled={isUploading}
                accept="image/*,application/pdf"
                onSelect={(newValue: any) => handlePickFile(row.rowId, newValue)}
              />

              {/* The workspace opens on its own when an image is picked; this
                  is how the operator gets BACK to it — to re-crop, or after
                  cancelling out of it. Images only: there is nothing to crop on
                  a PDF, and the tool works on a bitmap. */}
              {row.imageSrc && cropRowId !== row.rowId && (
                <button
                  type="button"
                  onClick={() => setCropRowId(row.rowId)}
                  disabled={isUploading}
                  className="self-start border border-slate-400 rounded px-3 py-1 text-[12px] text-slate-700 disabled:opacity-40"
                >
                  Crop / Adjust
                </button>
              )}

              {row.imageSrc && cropRowId === row.rowId && (
                <RowCropPanel
                  src={row.imageSrc}
                  onCropped={(dataUrl) => handleCropped(row, dataUrl)}
                  onClose={() => setCropRowId(null)}
                />
              )}

              {rowErrors[row.rowId] && (
                <div className="text-[12px] text-red-500">
                  {rowErrors[row.rowId]}
                </div>
              )}
            </div>
          ))}

          <button
            type="button"
            onClick={() => setRows((current) => [...current, newRow()])}
            disabled={isUploading}
            className="text-primary-main text-[13px] font-medium self-start disabled:opacity-40"
          >
            + Add another file
          </button>

          <div className="flex justify-end gap-3 pb-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isUploading}
              className="px-4 py-2 text-sm text-slate-600 disabled:opacity-40"
            >
              Cancel
            </button>
            <ATMLoadingButton
              isLoading={isUploading}
              onClick={handleSubmit}
              className="px-6"
            >
              Upload
            </ATMLoadingButton>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddMoreFileDialog;
